import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'lionfish-theme'
const QUERY = '(prefers-color-scheme: dark)'

const readStoredTheme = (): Theme | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : null
  } catch {
    return null
  }
}

const detectSystemTheme = (): Theme => {
  if (!window.matchMedia) return 'light'
  return window.matchMedia(QUERY).matches ? 'dark' : 'light'
}

type ThemeContextValue = {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

type ThemeProviderProps = {
  children: ReactNode
}

/**
 * Owns the `<html data-theme>` attribute that NXD's CSS variables (and
 * lionfish's own tokens in `globals.css`) key off. Follows the system
 * preference until the user toggles, after which the explicit choice is
 * persisted to localStorage and wins.
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [storedTheme, setStoredTheme] = useState<Theme | null>(readStoredTheme)
  const [systemTheme, setSystemTheme] = useState<Theme>(detectSystemTheme)

  useEffect(() => {
    if (!window.matchMedia) return
    const mql = window.matchMedia(QUERY)
    const onChange = () => setSystemTheme(mql.matches ? 'dark' : 'light')
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const theme = storedTheme ?? systemTheme

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme
  }, [theme])

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setStoredTheme(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage unavailable (private mode): the choice lasts for the session.
    }
  }, [theme])

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}

/**
 * Applies the persisted/system theme synchronously, before React renders,
 * so the first paint is already themed. (An inline <head> script would do
 * the same but the CSP forbids inline scripts.)
 */
export const applyInitialTheme = (): void => {
  document.documentElement.dataset['theme'] =
    readStoredTheme() ?? detectSystemTheme()
}
